import PropTypes from 'prop-types';
import React from 'react';
import UI from '../ui';
import Utils from '../utils';

const { BillingPlans } = Utils;
const { Link, List, InlineTable } = UI;
const { prettyPrintPlan } = BillingPlans;

/*
 * Transforms fee data from current and proposed plans into formatted table rows
 *
 * Example Input:
 * {
 *   currentPlanFees: {
 *     USPSPriorityFee: { FeePercent: 2.5, FeeMinValue: 1.0, FeeMaxValue: 5.0 },
 *     UPSGroundFee: 3.99
 *   },
 *   proposedPlanFees: {
 *     USPSPriorityFee: { FeePercent: 2.0, FeeMinValue: 1.0, FeeMaxValue: 4.0 },
 *     UPSGroundFee: 2.99
 *   }
 * }
 *
 * Example Output:
 * [
 *   { columns: ['USPS Priority Fee', '2.5%', '2.0%'] },
 *   { columns: ['UPS Ground Fee', '$3.99', '$2.99'] }
 * ]
 */
const transformFeesToTableRows = ({ currentPlanFees, proposedPlanFees }) => {
	// Formats fee values into human-readable strings with currency or percentage symbols
	const formatFeeDisplay = feeInfo => {
		if (typeof feeInfo !== 'object') {
			return feeInfo > 0 ? `$${feeInfo.toFixed(2)}` : 'No fee';
		}

		const {
			FeePercent: percentage = 0,
			FeeMinValue: minFee = 0,
			FeeMaxValue: maxFee = 0,
		} = feeInfo;

		if (percentage === 0 && minFee === 0 && maxFee === 0) return 'No fee'; // No fee applies
		if (minFee === maxFee && minFee > 0) return `$${minFee.toFixed(2)}`; // Flat rate fee
		if (maxFee === -1 && percentage > 0) return `${percentage}%`; // Percentage no upper bound
		if (percentage > 0) return `${percentage}%`; // Bounded percentage

		return '$0.00';
	};

	// Transforms camelCase fee identifiers into readable display names
	const formatFeeName = key => {
		const CARRIERS = ['USPS', 'UPS', 'DHL', 'GAP', 'GlobalPost'];

		const removePrefix = str => str.replace(/^Print/, '');
		const removeSuffix = str => str.replace(/Info$/, '');

		const extractCarrier = str => {
			const foundCarrier = CARRIERS.find(carrierName =>
				str.startsWith(carrierName)
			);
			return foundCarrier
				? { carrier: foundCarrier, remaining: str.slice(foundCarrier.length) }
				: { carrier: '', remaining: str };
		};
		const splitIntoWords = str => str.split(/(?=[A-Z])/).filter(Boolean);

		const cleanedKey = removeSuffix(removePrefix(key));
		const { carrier, remaining } = extractCarrier(cleanedKey);
		const words = carrier
			? [carrier, ...splitIntoWords(remaining)]
			: splitIntoWords(remaining);

		return words.join(' ').trim();
	};

	// Validates if a fee object contains non-zero percentage or flat rate values
	const isFeeObjectValid = value => {
		if (typeof value !== 'object') return false;

		const {
			FeePercent: percentage = 0,
			FeeMinValue: minFee = 0,
			FeeMaxValue: maxFee = 0,
		} = value;

		return percentage > 0 || (minFee === maxFee && minFee > 0);
	};

	// Compares two fee values to determine if they are different
	const hasDifferentFeeValues = (current, proposed) => {
		if (typeof current === 'object' && typeof proposed === 'object') {
			return (
				current.FeePercent !== proposed.FeePercent ||
				current.FeeMinValue !== proposed.FeeMinValue ||
				current.FeeMaxValue !== proposed.FeeMaxValue
			);
		}
		return current !== proposed;
	};

	// Creates a structured fee entry object
	const createFeeEntry = (key, current, proposed) => {
		const currentValue = (current.StampFees || current)[key];
		const proposedValue = proposed[key];

		return {
			name: formatFeeName(key),
			firstColumn: formatFeeDisplay(currentValue),
			secondColumn: formatFeeDisplay(proposedValue),
		};
	};

	const fees = currentPlanFees.StampFees || currentPlanFees;

	const isValidFee = ([_, value]) =>
		typeof value === 'object' ? isFeeObjectValid(value) : value > 0;

	const createEntry = ([key]) =>
		createFeeEntry(key, currentPlanFees, proposedPlanFees);

	const hasDifferentValues = entry =>
		hasDifferentFeeValues(entry.firstColumn, entry.secondColumn);

	const rows = Object.entries(fees)
		.filter(isValidFee)
		.map(createEntry)
		.filter(hasDifferentValues)
		.map(entry => ({
			columns: [entry.name, entry.firstColumn, entry.secondColumn],
		}));

	return rows;
};

// Displays features and benefits of the proposed subscription plan with a "much more" link
const ProposedPlanFeatures = ({ proposedPlanName, descriptionText }) => {
	const getMuchMoreLink = () => (
		<span key="much-more">
			And{' '}
			<Link
				href="https://www.stamps.com/pricing/"
				target="_blank"
				rel="noopener noreferrer"
			>
				much more
			</Link>
		</span>
	);

	const getFeaturesList = () => {
		const features = descriptionText
			.split(' - ')
			.map(item => item.trim())
			.filter(Boolean)
			.slice(2);

		return [...features, getMuchMoreLink()];
	};

	return (
		<List
			label={`Changing to the ${prettyPrintPlan(proposedPlanName)} plan gives you access to:`}
			items={getFeaturesList()}
			icon="affirm"
		/>
	);
};

const createPlanComparisonTable = (currentPlan, proposedPlan) => {
	const rows = transformFeesToTableRows({
		currentPlanFees: currentPlan.fees,
		proposedPlanFees: proposedPlan.fees,
	});

	if (rows.length === 0) {
		return { component: null, hasTable: false };
	}

	const tableData = {
		header: [
			'Fee Type',
			'Your Plan',
			`${prettyPrintPlan(proposedPlan.name)} Plan`,
		],
		rows,
		footer: [
			'Monthly Subscription',
			`$${currentPlan.price} /month`,
			`$${proposedPlan.price} /month`,
		],
	};

	return {
		component: <InlineTable {...tableData} />,
		hasTable: true,
	};
};

const PlanPriceComparison = ({ data }) => {
	const { upgradePlan } = data;

	const currentPlan = {
		fees: BillingPlans.getCurrentPlan().StampFees,
		price: BillingPlans.getCurrentPlan().Price,
		name: BillingPlans.getCurrentPlan().Name,
	};

	const proposedPlan = {
		fees: upgradePlan.StampFees,
		price: upgradePlan.Price,
		name: upgradePlan.Name,
	};

	const { component: planComparisonTable, hasTable } =
		createPlanComparisonTable(currentPlan, proposedPlan);

	return (
		<div className={`upgrade-plan-comparison ${hasTable ? '' : 'no-table'}`}>
			<ProposedPlanFeatures
				proposedPlanName={upgradePlan.Name}
				descriptionText={upgradePlan.Description}
			/>
			{planComparisonTable}
		</div>
	);
};

// PropTypes for the fee structure
const feeShape = PropTypes.oneOfType([
	PropTypes.number,
	PropTypes.shape({
		FeePercent: PropTypes.number,
		FeeMinValue: PropTypes.number,
		FeeMaxValue: PropTypes.number,
	}),
]);

// PropTypes for the plan structure
const planShape = PropTypes.shape({
	StampFees: PropTypes.objectOf(feeShape).isRequired,
	Name: PropTypes.string.isRequired,
	Price: PropTypes.number.isRequired,
	Description: PropTypes.string.isRequired,
});

// PropTypes for ProposedPlanFeatures component
ProposedPlanFeatures.propTypes = {
	proposedPlanName: PropTypes.string.isRequired,
	descriptionText: PropTypes.string.isRequired,
};

// PropTypes for transformFeesToTableRows function
transformFeesToTableRows.propTypes = {
	currentPlanFees: PropTypes.objectOf(feeShape).isRequired,
	proposedPlanFees: PropTypes.objectOf(feeShape).isRequired,
};

// Main component PropTypes
PlanPriceComparison.propTypes = {
	data: PropTypes.shape({
		upgradePlan: planShape.isRequired,
	}).isRequired,
};

export default PlanPriceComparison;
